import React from 'react'
import { useNavigate } from 'react-router-dom'
import { LogOut, Zap, Building, Users, Menu, MessageSquare, Globe } from 'lucide-react'
import { useLanguage } from '@/contexts/LanguageContext'
import Button from '@/components/Button'
import deepWindLogo from '@/assets/deepwind-logo.png'
import type { useTurbineDetailViewModel } from '../useTurbineDetailViewModel'

type VM = ReturnType<typeof useTurbineDetailViewModel>

interface DetailHeaderProps {
  isSuperAdmin: VM['isSuperAdmin']
  isMobileMenuOpen: VM['isMobileMenuOpen']
  setIsMobileMenuOpen: VM['setIsMobileMenuOpen']
  isCommentsOpen: VM['isCommentsOpen']
  setIsCommentsOpen: VM['setIsCommentsOpen']
}

const DetailHeader: React.FC<DetailHeaderProps> = ({
  isSuperAdmin,
  isMobileMenuOpen,
  setIsMobileMenuOpen,
  isCommentsOpen,
  setIsCommentsOpen,
}) => {
  const navigate = useNavigate()
  const { t, language, setLanguage } = useLanguage()

  return (
    <>
      <header className="bg-white shadow-sm border-b relative" style={{ pointerEvents: 'auto' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" style={{ pointerEvents: 'auto' }}>
          <div className="flex justify-between items-center h-16" style={{ pointerEvents: 'auto' }}>
            <div className="flex items-center space-x-4">
              <div className="flex items-center space-x-2">
                <div className="w-8 h-8 rounded-lg flex items-center justify-center">
                  <img 
                    src={deepWindLogo} 
                    alt="DeepWind Logo" 
                    className="w-8 h-8 object-contain"
                  />
                </div>
                <div>
                  <h1 className="text-xl font-bold text-gray-900">{t.dashboard.companyName}</h1>
                  <p className="text-xs text-gray-500">{t.dashboard.turbineManagement}</p>
                </div>
              </div>
              

              
            </div>
            
            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center space-x-3">
              
              {/* Comments Button - Same position as 360 view */}
              <Button
                onClick={() => setIsCommentsOpen(!isCommentsOpen)}
                className="bg-green-500 hover:bg-green-600 text-white"
                size="sm"
              >
                <MessageSquare className="w-4 h-4 mr-2" />
                {t.turbineDetail.comments.title}
              </Button>
              
              <Button
                onClick={() => navigate('/dashboard')}
                className="bg-blue-600 hover:bg-blue-700 text-white"
                size="sm"
              >
                <Zap className="w-4 h-4 mr-2" />
                {t.dashboard.turbineManagement}
              </Button>
              
              {isSuperAdmin && (
              <Button
                onClick={() => navigate('/dashboard/companies')}
                className="bg-purple-600 hover:bg-purple-700 text-white"
                size="sm"
              >
                <Building className="w-4 h-4 mr-2" />
                {t.dashboard.partnerManagement}
              </Button>
              )}
              
              {isSuperAdmin && (
              <Button
                onClick={() => navigate('/dashboard/users')}
                className="bg-green-600 hover:bg-green-700 text-white"
                size="sm"
              >
                <Users className="w-4 h-4 mr-2" />
                {t.dashboard.userSettings}
              </Button>
              )}
              
              {/* Language Toggle Button */}
              <Button
                onClick={() => {
                  const newLanguage = language === 'tr' ? 'en' : 'tr'
                  setLanguage(newLanguage)
                }}
                variant="outline"
                size="sm"
                className="hover:bg-blue-50 hover:border-blue-200 hover:text-blue-600 transition-colors"
                title={language === 'tr' ? 'Switch to English' : 'Türkçe\'ye Geç'}
              >
                <Globe className="w-4 h-4 mr-2" />
                {language === 'tr' ? 'EN' : 'TR'}
              </Button>
              
              {/* Cikis yerel token'i dogrudan siler; backend'e istek gitmedigi icin
                  basarisiz olabilecek bir islem YOK, dolayisiyla gosterilecek hata da yok.
                  (VM'deki `logout()` backend revocation da yapar ama bu buton onu kullanmiyor;
                  akisi degistirmemek icin oldugu gibi birakildi.) */}
              <Button
                onClick={() => {
                  localStorage.removeItem('deepwind-auth-token')
                  localStorage.removeItem('deepwind-user')
                  navigate('/')
                }}
                variant="outline"
                size="sm"
                className="hover:bg-red-50 hover:border-red-200 hover:text-red-600 transition-colors"
              >
                <LogOut className="w-4 h-4 mr-2" />
                {t.dashboard.logout}
              </Button>
            </div>

            {/* Mobile Menu Button */}
            <div className="lg:hidden">
              <Button
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                variant="outline"
                size="sm"
                className="p-2"
              >
                <Menu className="w-5 h-5" />
              </Button>
          </div>
          </div>

          {/* Mobile Menu Dropdown */}
          {isMobileMenuOpen && (
            <div className="lg:hidden absolute top-full left-0 right-0 bg-white border-t border-gray-200 shadow-lg z-[9999] w-full">
              <div className="p-4 space-y-2">
                <Button
                  onClick={() => {
                    navigate('/dashboard')
                    setIsMobileMenuOpen(false)
                  }}
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white justify-start"
                  size="sm"
                >
                  <Zap className="w-4 h-4 mr-2" />
                  {t.dashboard.turbineManagement}
                </Button>
                {isSuperAdmin && (
                  <Button
                    onClick={() => {
                      navigate('/dashboard/companies')
                      setIsMobileMenuOpen(false)
                    }}
                    className="w-full bg-purple-600 hover:bg-purple-700 text-white justify-start"
                    size="sm"
                  >
                    <Building className="w-4 h-4 mr-2" />
                    {t.dashboard.partnerManagement}
                  </Button>
                )}
                {isSuperAdmin && (
                  <Button
                    onClick={() => {
                      navigate('/dashboard/users')
                      setIsMobileMenuOpen(false)
                    }}
                    className="w-full bg-green-600 hover:bg-green-700 text-white justify-start"
                    size="sm"
                  >
                    <Users className="w-4 h-4 mr-2" />
                    {t.dashboard.userSettings}
                  </Button>
                )}
                
                {/* Language Toggle Button for Mobile */}
                <Button
                  onClick={() => {
                    const newLanguage = language === 'tr' ? 'en' : 'tr'
                    setLanguage(newLanguage)
                  }}
                  variant="outline"
                  size="sm"
                  className="w-full hover:bg-blue-50 hover:border-blue-200 hover:text-blue-600 transition-colors justify-start"
                >
                  <Globe className="w-4 h-4 mr-2" />
                  {language === 'tr' ? 'Switch to English' : 'Türkçe\'ye Geç'}
                </Button>
                
                <Button
                  onClick={() => {
                    localStorage.removeItem('deepwind-auth-token')
                    localStorage.removeItem('deepwind-user')
                    navigate('/')
                  }}
                  variant="outline"
                  size="sm"
                  className="w-full hover:bg-red-50 hover:border-red-200 hover:text-red-600 transition-colors justify-start"
                >
                  <LogOut className="w-4 h-4 mr-2" />
                  {t.dashboard.logout}
                </Button>
              </div>
            </div>
          )}
        </div>
      </header>
    </>
  )
}

export default DetailHeader
